// tslint:disable:no-empty-interface
// tslint:disable:max-line-length
// tslint:disable: no-use-before-declare
import { Params } from "@angular/router";
import { ITCIAppAutoScaleConfig } from "./tci-app-detail-env";
import { ITCIApplication } from "./tci-application";
import { Observable, Subject } from "rxjs";
import { ITCITenantComponentInput } from "./tci-component";
import {
    ITCIApplicationErrorInfo,
    ITCIApplicationRequest,
    ITCIApplicationResponse,
    ITCIAppStatusCategory,
    ITCITenantServiceProvider,
    TCI_APPLICATION_ERR_TYPE
} from "./tci-tenant";
import * as lodash from "lodash";

export const enum TCIApplicationDeploymentNotification {
    /**
     * Refresh notification
     */
    NOTIFICATION_REFRESH = 0,
    /**
     * Progress notification
     */
    NOTIFICATION_PROGRESS = 1,
    /**
     * Reporting the deployment status of an app
     */
    NOTIFICATION_STATUS = 2,
    /**
     * Reporting the pushAppChanges callback function output
     */
    NOTIFICATION_RESULT_APP_CHANGES = 3,
    /**
     * Reporting app force stop when inifinite scaling loop
     */
    NOTIFICATION_FORCE_STOP = 4,
}

export const enum TCIApplicationDeploymentMode {
    MODE_APP_LIST,
    MODE_APP_DETAILS,
    MODE_APP_HAM_MENU_COPY,
    MODE_APP_HAM_MENU_REPLACE,
    MODE_APP_HAM_MENU_MARK_LIVE,
    MODE_APP_DETAILS_ENV_CTRL
}

export const enum TCIApplicationDeployConfirmationType {
    CONFIRM_DEFAULT,
    CONFIRM_IS_ALIVE
}

/**
 * interface ITCIApplicationDeployRequest
 */
export interface ITCIApplicationDeployRequest extends ITCIApplicationRequest {
    /**
     * Set the UI invocation mode
     */
    mode: TCIApplicationDeploymentMode;
    /**
     * Set the number of instances
     */
    num_instances?: number;
    /**
     * Determines if a scaling action is increasing the instances count
     */
    scale_direction?: boolean;
    /**
     * Determines if auto scale is turned on or off
     */
    auto_scale?: boolean;
    /**
     * force stop an application
     */
    force_stop?: boolean;
    /**
     * Error information
     */
    error?: ITCIApplicationErrorInfo;
    /**
     * Confirmation Type
     */
    confirmationType?: TCIApplicationDeployConfirmationType;
    /**
     * auto scale config
     */
    autoScaleConfig?: ITCIAppAutoScaleConfig;
}

/**
 * class TCI_APP_STATUS_CATEGORY
 */
export class TCIAppStatusCategory implements ITCIAppStatusCategory {
    public static INFO = new TCIAppStatusCategory("#f4f4f4", "ng-deploy-state-info", TCI_APPLICATION_ERR_TYPE.ERR_TYPE_INFO, "ng-dpl-info");
    public static ERROR = new TCIAppStatusCategory("#f9e1e4", "ng-deploy-state-error", TCI_APPLICATION_ERR_TYPE.ERR_TYPE_ERROR, "ng-dpl-error");
    public static WARNING = new TCIAppStatusCategory("#ffe9ba", "ng-deploy-state-warning", TCI_APPLICATION_ERR_TYPE.ERR_TYPE_WARN, "ng-dpl-warning");
    public static IN_PROGRESS = new TCIAppStatusCategory("#e0f0f9", "ng-deploy-state-progress", TCI_APPLICATION_ERR_TYPE.ERR_TYPE_INFO, "ng-dpl-in-progress");
    public static READY = new TCIAppStatusCategory("#e1f7eb", "ng-deploy-state-ready", TCI_APPLICATION_ERR_TYPE.ERR_TYPE_INFO, "ng-dpl-ready");
    public static NOT_READY = new TCIAppStatusCategory("#f4f4f4", "ng-deploy-state-not-ready", TCI_APPLICATION_ERR_TYPE.ERR_TYPE_INFO, "ng-dpl-not-ready");
    public static INCOMPLETE = new TCIAppStatusCategory("#f4f4f4", "ng-deploy-state-info", TCI_APPLICATION_ERR_TYPE.ERR_TYPE_INFO, "ng-dpl-incomplete");
    public static ANIMATED_PENDING = new TCIAppStatusCategory("", "ng-deploy-state-loading", TCI_APPLICATION_ERR_TYPE.ERR_TYPE_INFO, "ng-dpl-ani-pending");
    public static UNKNOWN = new TCIAppStatusCategory("", "", TCI_APPLICATION_ERR_TYPE.ERR_TYPE_UNKNOWN, "");

    private constructor(private color: string, private iconUrl: string, private errorType: TCI_APPLICATION_ERR_TYPE, private className: string) {
    }

    get Color(): string {
        return this.color;
    }

    get IconUrl() {
        return this.iconUrl;
    }

    get ErrorType() {
        return this.errorType;
    }

    get ClassName() {
        return this.className;
    }
}

export interface ITCIApplicationDeploymentStatus {

    getLabel(): string;

    getCategory(): TCIAppStatusCategory;

    getIconUrl(): string;

    getColor(): string;

    getClassName(): string;
}

/**
 * Normalized TCI Application Deployment Status
 */
export class TCIApplicationDeploymentStatus implements ITCIApplicationDeploymentStatus {

    constructor(
        private label: string,
        private category: TCIAppStatusCategory = TCIAppStatusCategory.INFO) {
    }

    getLabel(): string {
        return this.label;
    }

    getCategory(): TCIAppStatusCategory {
        return this.category;
    }

    getIconUrl(): string {
        return this.category && this.category.IconUrl;
    }

    getColor(): string {
        return this.category && this.category.Color;
    }

    getClassName(): string {
        return this.category && this.category.ClassName;
    }

    toString(): string {
        return this.label;
    }
}

export class TCIAppActionStage {
    public static UNKNOWN = new TCIAppActionStage("unknown");
    public static CONFIGURE = new TCIAppActionStage("configure");
    public static COMPLETED = new TCIAppActionStage("completed");
    public static SCALE = new TCIAppActionStage("scale");
    public static PUSH = new TCIAppActionStage("push");
    public static fromString(str: string): TCIAppActionStage {
        switch (str) {
            case "configure":
                return TCIAppActionStage.CONFIGURE;
            case "completed":
                return TCIAppActionStage.COMPLETED;
            case "scale":
                return TCIAppActionStage.SCALE;
            case "push":
                return TCIAppActionStage.PUSH;
            default:
                return TCIAppActionStage.UNKNOWN;
        }
    }
    private constructor(private val: string) {
    }
    public equals(that: any): boolean {
        // Shortcut for trivial case
        if (this === that) {
            return true;
        }
        // check for primitive
        if (typeof that === "string" && that === this.val) {
            return true;
        }

        if (that.val && typeof that.val === "string") {
            return this.val === that.val;
        }

        if (!(that instanceof TCIAppActionStage)) {
            return false;
        }
        // If two sets don't have the same size, they're not equal

        if (this.toString() !== that.toString()) {
            return false;
        }

        return lodash.isEqual(this, that);
    }

    public toString(): string {
        return this.val;
    }
}

/**
 * ITCIApplicationDeployResponse
 */
export interface ITCIApplicationDeployResponse extends ITCIApplicationResponse {
    /**
     * app action stage
     */
    action?: TCIAppActionStage;
    /**
     * action success or failure
     */
    success: boolean;
    /**
     * Get the action type that was sent in the request
     */
    notificationType: TCIApplicationDeploymentNotification;
    /**
     * Returns the Status
     */
    status?: ITCIApplicationDeploymentStatus;
    /**
     * Returns the number of instances running
     */
    instances?: number;
    /**
     * Returns the initial number of instances in starting process
     */
    startingInstances?: number;
    /**
     * Returns the number of failed instances
     */
    failedInstances?: number;
    /**
     * Returns the number of desired instances
     */
    desiredInstances?: number;
    /**
     * Returns the number of vpn instances
     */
    vpnInstances?: number;
    /**
     * Confirmation Type
     */
    confirmationType?: TCIApplicationDeployConfirmationType;
    /**
     * Details msg or error details
     */
    details?: string;
    /**
     * Liveapps app status from latestGovernanceState
     */
    liveAppsStatus?: string;

    /**
     * Handle CompueNodeError in CIC2
     */
    computeNodeError?: boolean;
    /**
     * scribe app last run time
     */
    lastRunTime?: number;
}

/**
 * interface ITCIApplicationDeploymentMiniDetails
 */
export interface ITCIApplicationDeploymentMiniDetails {
    /**
     * HTML string to place at mini-details container
     */
    contentText: string;

    /**
     * ID of the element to attach a route-navigation click event.
     * This ID should be present at "contentText" HTML.
     */
    clickElementId: string;

    /**
     * Arguments to be used for route-navigation when click
     * happens at the "clickElementId" HTML element.
     */
    clickRouteParams: { route: string[], queryParams?: Params };
}

/**
 * interface ITCIApplicationDeploymentProvider
 */
export interface ITCIApplicationDeploymentProvider extends ITCITenantServiceProvider {
    /**
     * Tell TCI deployer UI whether to show confirmation popup
     * @param input
     */
    canShowConfirmationDialog(input: ITCIApplicationDeployRequest): Observable<ITCIApplicationDeployResponse>;

    /**
     * Show Start/Stop widgets from being displayed
     * @param input
     * @return @Observable<boolean>
     */
    canShowStartStop(input: ITCIApplicationDeployRequest): Observable<boolean>;

    /**
     * Get the start state information whether the application can be started
     * @param input
     * @return @Observable<boolean>
     */
    canStart(input: ITCIApplicationDeployRequest): Observable<boolean>;

    /**
     * starts the application
     * @param input
     * @return Observable<ITCIApplicationDeployResponse>
     */
    start(input: ITCIApplicationDeployRequest): Observable<ITCIApplicationDeployResponse>;

    /**
     * Get the stop state information whether the application can be stopped
     * @param input
     * @return @Observable<boolean>
     */
    canStop(input: ITCIApplicationDeployRequest): Observable<boolean>;

    /**
     * stops the application
     * @param input
     * @return Observable<ITCIApplicationDeployResponse>
     */
    stop(input: ITCIApplicationDeployRequest): Observable<ITCIApplicationDeployResponse>;

    /**
     * This is an explicit call to trigger a callback deployer action which is expected
     * to do something that changes the running instances behavior e.g. scaling up/down/stop/start
     * which may be triggered by AO api. This function is wrapping the AO call by waiting
     * for the App Status notification to be triggered via the refresh timer loop and
     * pass the completion status notifications back to the caller with callback
     * results and intermediate and end statuses.The Caller must subscribe for notifications.
     * @param input: @ITCIApplicationDeployRequest
     * @param callback: (cinput: @ITCIApplicationDeployRequest) => @Observable<@ITCIApplicationDeployResponse>
     * @return @Observable<@ITCIApplicationDeployResponse>
     */
    pushAppChanges(input: ITCIApplicationDeployRequest, callback: (cinput: ITCIApplicationDeployRequest) => Observable<ITCIApplicationDeployResponse>): Observable<ITCIApplicationDeployResponse>;

    /**
     * Return state if the provider is busy waiting for the pushAppChanges to finish
     * @param input
     */
    isPushingAppChanges(input: ITCIApplicationDeployRequest): Observable<boolean>;

    /**
     * Return an observable indicating if the deployer is busy or not
     * The deployer is busy when pushingAppChanges/start/stop/scale is happening
     * and also when it gets a busy indication from polling async app manager
     * app status for tci apps and it is the responsibility of the tenant provider
     * to let the TCI infrastructure know when it is busy
     * @param input
     */
    isBusy(input: ITCIApplicationDeployRequest): Observable<boolean>;

    /**
     * Return an observable<boolean> if the UI should behave in autoscaling mode
     * In this mode only the number of instances are to be captured and saved
     * in domain server for TCI apps and API orchestrator needs to turn on
     * auto scaling mode for existing / running apps. For Scribe there is no
     * Autoscaling and it should be false always
     * @param input
     */
    canAutoScale(input: ITCIApplicationDeployRequest): Observable<boolean>;

    /**
     * update auto scale configuration
     * @param input
     */
    updateAutoScale(input: ITCIApplicationDeployRequest): void;

    /**
     * This is an application deployment state snapshot at any given point of time.
     * The caller should not make state dependant UI changes using this state as
     * it will dynamically change.
     * @param app Application instance requesting the data
     */
    getStatusSnapshot(app: ITCIApplication): ITCIApplicationDeployResponse;

    /**
     * Before subscribing this method will be called to let the provider know
     * the consumer is subscribed
     * @param app: @ITCIApplication
     * @return @Observable<boolean>
     */
    startStatusSubscription(app: ITCIApplication): Observable<boolean>;

    /**
     * Before unsubscribing this method will be called to let the provider know
     * the consumer is unsubscribed
     * @param app: @ITCIApplication
     * @return @Observable<boolean>
     */
    stopStatusSubscription(app: ITCIApplication): Observable<boolean>;

    /**
     * Returns deploymentStatus Subject, the UI consumer will subscribe and unsubscribe from this
     * subject when the UI is active and inactive respectively. The provider's responsibility is to
     * control how frequently it needs to emit on the subject. There is only one subject per provider
     * which will emit per App status change with app Id in the @ITCIApplicationDeployResponse. The
     * subscriber will update the respective app status based on the response.
     *
     * Important: It's tenant responsibility to catch errors that might happen during the status check
     * for this subject to return data reflecting such cases.
     *
     * @return Subject<ITCIApplicationDeployResponse>
     */
    getStatusSubject(): Subject<ITCIApplicationDeployResponse>;

    /**
     * checks if the provider can scale apps
     * @param input @ITCIApplicationDeployRequest
     * @return @Observable<boolean>
     */
    canScale(input: ITCIApplicationDeployRequest): Observable<boolean>;

    /**
     * Sets number of instances by given count i.e. adds to or subtract from the existing number of instances
     * The number of instances can range from 0 to max allowed instances
     * @return @Observable<IApplicationDeployResponse>
     */
    setInstances(input: ITCIApplicationDeployRequest): Observable<ITCIApplicationDeployResponse>;

    /**
     * Gets the instance scaling value for initialization
     * @param input
     */
    getInstances(input: ITCIApplicationDeployRequest): Observable<ITCIApplicationDeployResponse>;

    /**
     * checks if the details badge can be shown.
     * The details  badge will appear and onclick it will open a popup
     * which will load a tenant component implementing
     * TCI_APP_LIST_DETAIL_BADGE = new TCITenantSelectorType("tci-app-list-detail-badge");
     * @param input
     */
    canShowDetails(input: ITCIApplicationDeployRequest): Observable<boolean>;

    /**
     * The details popup is dynamic component loader which needs input to pass
     * to the tenant component to initialize. Each tenant provider will determing what input
     * it needs by extending ITCITenantComponentInput to its component input binding.
     * @param input
     * @deprecated
     */
    getDetailsInput<T extends ITCITenantComponentInput>(input: ITCIApplicationDeployRequest): Observable<T>;

    /**
    * The details popup will no longer be a dynamic tenant component. The popup component will
    * display the HTML content returned by this function. The HTML content cannot have any forms
    * events, actions. Any resource links like images need to have absolute URLS resolved by the
    * tenant microservice static files.
    * @param input
    */
    getDetailContent(input: ITCIApplicationDeployRequest): Observable<string | ITCIApplicationDeploymentMiniDetails>;

    /**
     * The details popup overlay includes a button for the user to click and copy the content
     * into the clipboard. This method returns the text that would be copied.
     * The text should not include HTML tags.
     * @param input
     */
    getDetailTextForClipboard(input: ITCIApplicationDeployRequest): Observable<string>;

    /**
     * Check if there is a status error to show the red dot badge on status.
     * @param input
     */
    showStatusError(input: ITCIApplicationDeployRequest): Observable<boolean>;

    /**
     * This will be triggered when "pushAppChanges" callback have finished the
     * execution with the intention of restarting the deployment state check
     */
    refresh(request: ITCIApplicationDeployRequest): void;

    /**
     * Returns the Subject that would emit the status of the application to
     * notify UI. The check loop would call "next" on the subject on status change
     * @param request
     */
    getOnPremiseAppStatusSubject(request: ITCIApplicationDeployRequest): Subject<ITCIApplicationDeployResponse>;

    /**
     * Returns the Subject that would emit the status of the application to
     * notify UI. The check loop would call "next" on the subject on status change
     */
     getOnPremiseApplicationStatusSubject(): Subject<ITCIApplicationDeployResponse>;

    /**
     * Before subscribing this method will be called to let the provider know
     * the consumer is subscribed
     * @param app: @ITCIApplication
     * @return @Observable<boolean>
     */
    startOnPremiseStatusSubscription(app: ITCIApplication): Observable<boolean>;

    /**
     * Before unsubscribing this method will be called to let the provider know
     * the consumer is unsubscribed
     * @param app: @ITCIApplication
     * @return @Observable<boolean>
     */
    stopOnPremiseStatusSubscription(app: ITCIApplication): Observable<boolean>;
}

/**
 * Possible TCI Application Deployment Stages
 */
export const TCI_APP_DEPLOYMENT_STAGE = {
    PUBLISHED: "published",
    DRAFT: "draft"
};
export type TCI_APP_DEPLOYMENT_STAGE = keyof typeof TCI_APP_DEPLOYMENT_STAGE;

export interface ITCIApplicationPublishRequest extends ITCIApplicationRequest {
    msg: string;
    stage: TCI_APP_DEPLOYMENT_STAGE;
}

export interface ITCIApplicationPublishResponse extends ITCIApplicationResponse {
    msg: string;
    success: boolean;
    stage: TCI_APP_DEPLOYMENT_STAGE;
}

export interface ITCIApplicationPublishToMasheryResponse extends ITCIApplicationResponse {
    msg: string;
    success: boolean;
    result: any;
}

export class TCILiveappsDeploymentStatus {
    public static DRAFT = new TCILiveappsDeploymentStatus("DRAFT");
    public static PUBLISHED = new TCILiveappsDeploymentStatus("PUBLISHED");
    public static READY_TO_PUBLISH = new TCILiveappsDeploymentStatus("READY TO PUBLISH");
    public static WITHDRAWN = new TCILiveappsDeploymentStatus("WITHDRAWN");
    public static NEEDS_UPDATE = new TCILiveappsDeploymentStatus("NEEDS UPDATE");

    private constructor(private _str: string) {
    }

    getString() {
        return this._str;
    }
}

export class TCILiveappsDeploymentStatusLabel {
    public static DRAFT = new TCILiveappsDeploymentStatusLabel("Draft");
    public static PUBLISHED = new TCILiveappsDeploymentStatusLabel("Published");
    public static READY_TO_PUBLISH = new TCILiveappsDeploymentStatusLabel("Ready To Publish");
    public static WITHDRAWN = new TCILiveappsDeploymentStatusLabel("Withdrawn");
    public static NEEDS_UPDATE = new TCILiveappsDeploymentStatusLabel("Needs Update");

    private constructor(private _str: string) {
    }

    getString() {
        return this._str;
    }
}

export class TCILiveappsStatus {
    public static mapILAApplicationStatusToDeploymentStatus(str: string): string {
        switch (str) {
            case TCILiveappsDeploymentStatus.DRAFT.getString():
                return TCILiveappsDeploymentStatusLabel.DRAFT.getString();
            case TCILiveappsDeploymentStatus.PUBLISHED.getString():
                return TCILiveappsDeploymentStatusLabel.PUBLISHED.getString();
            case TCILiveappsDeploymentStatus.READY_TO_PUBLISH.toString():
                return TCILiveappsDeploymentStatusLabel.READY_TO_PUBLISH.getString();
            case TCILiveappsDeploymentStatus.WITHDRAWN.toString():
                return TCILiveappsDeploymentStatusLabel.WITHDRAWN.getString();
            case TCILiveappsDeploymentStatus.NEEDS_UPDATE.toString():
                return TCILiveappsDeploymentStatusLabel.NEEDS_UPDATE.getString();
            default:
                return TCILiveappsDeploymentStatusLabel.DRAFT.getString();
        }
    }
    public static mapILAApplicationStatusToDeploymentCategory(str: string): TCIAppStatusCategory {
        switch (str) {
            case TCILiveappsDeploymentStatus.DRAFT.toString():
                return TCIAppStatusCategory.INCOMPLETE;
            case TCILiveappsDeploymentStatus.PUBLISHED.toString():
                return TCIAppStatusCategory.READY;
            case TCILiveappsDeploymentStatus.READY_TO_PUBLISH.toString():
                return TCIAppStatusCategory.IN_PROGRESS;
            case TCILiveappsDeploymentStatus.WITHDRAWN.toString():
                return TCIAppStatusCategory.NOT_READY;
            case TCILiveappsDeploymentStatus.NEEDS_UPDATE.toString():
                return TCIAppStatusCategory.ERROR;
            default:
                return TCIAppStatusCategory.INCOMPLETE;
        }
    }
    private constructor() {
    }
}
